<template>
  <v-card class="
      max-h-content-container
      app-chat
      position-relative
      overflow-hidden
      text-sm
    ">
    <!-- Navigation Drawer: Left Sidebar -->
    <v-navigation-drawer v-model="isLeftSidebarOpen" width="374" touchless :right="$vuetify.rtl" mobile-breakpoint="sm"
      :temporary="$vuetify.breakpoint.xsOnly" absolute>
      <chat-left-sidebar-content :channels="channels" :managed-channels="managedPrivateChannels"
        :view-managed="viewManaged" :selected-channel="selectedManagedChannel" :managed-rooms="managedRooms"
        :activeChatRoom="activeChatRoom ? activeChatRoom : null" @managed-channel-click="getManagedChannelRooms"
        @my-chats-click="viewChannels" @open-channel="openChannel" @open-managed-room="openManagedRoom"
        @on-search-result-click="startChatWithUser" :chats-contacts="chatsContacts" :contacts="contacts"
        :active-chat-contact-id="activeChat.contact ? activeChat.contact.id : null
          " :profile-user-minimal-data="profileUserDataMinimal" @close-left-sidebar="isLeftSidebarOpen = false"
        @open-chat="openChatOfContact" @open-chat-user-profile-sidebar-content="
          isUserProfileSidebarOpen = true
          "></chat-left-sidebar-content>
    </v-navigation-drawer>

    <!-- Navigation Drawer: User Profile -->
    <v-navigation-drawer v-model="isUserProfileSidebarOpen" width="374" absolute :right="$vuetify.rtl" touchless
      temporary>
      <chat-user-profile-sidebar-content :profile-user-data="profileUserData"
        @close-user-profile-sidebar="isUserProfileSidebarOpen = false"></chat-user-profile-sidebar-content>
    </v-navigation-drawer>

    <!-- Navigation Drawer: Active Chat -->
    <v-navigation-drawer v-model="isActiveChatUserProfileSidebarOpen" width="374" absolute temporary
      :right="!$vuetify.rtl" touchless>
      <chat-active-chat-user-profile-sidebar-content v-if="activeChat.contact" :profile-user-data="activeChat.contact"
        @close-active-chat-user-profile-sidebar="
          isActiveChatUserProfileSidebarOpen = false
          "></chat-active-chat-user-profile-sidebar-content>
    </v-navigation-drawer>
    <div class="chat-content-area h-full d-flex flex-column" :style="contentStyles">
      <!-- Active Chat -->
      <div v-if="activeChatRoom" class="h-full">
        <!-- Navbar -->
        <div class="d-flex align-center justify-space-between px-5 py-4" style="height: 72px">
          <perfect-scrollbar :options="perfectScrollbarOptions">
            <!-- Sidebar Toggler, Avatar & Name -->
            <div class="d-flex align-center">
              <v-btn icon class="me-2 d-inline-block d-md-none">
                <v-icon v-show="!isLeftSidebarOpen" @click="isLeftSidebarOpen = true">
                  {{ icons.mdiMenu }}
                </v-icon>
              </v-btn>

              <v-avatar v-if="activeChatRoom" size="38" class="cursor-pointer me-3" :class="`white`"
                @click="isActiveChatUserProfileSidebarOpen = true">
                <v-img v-if="activeChatRoom.image" :src="activeChatRoom.image"></v-img>
                <v-icon v-else color="primary" class="pa-1">
                  {{ icons.mdiAccountOutline }}
                </v-icon>
              </v-avatar>

              <div class="d-flex flex-column">
                <p class="mt-0 mb-0 text-xl text--primary font-weight-medium">
                  {{ activeChatRoom.title }}
                </p>
                <v-tooltip bottom max-width="400px" color="primary">
                  <template v-slot:activator="{ on, attrs }">
                    <span v-bind="attrs" v-on="on" class="
                        text--disabled text-xs text-truncate
                        d-inline-block
                        text-truncate
                      " style="max-width: 800px">
                      {{ activeChatRoom.description }}
                    </span>
                  </template>
                  <span style="white-space: pre-line">
                    {{ activeChatRoom.description }}
                  </span>
                </v-tooltip>
              </div>
            </div>
          </perfect-scrollbar>
          <!-- Active Chat Actions -->
          <!-- <div class="d-flex align-baseline">
            <v-icon class="me-3 cursor-pointer d-none d-sm-inline-flex">
              {{ icons.mdiInformation }}
            </v-icon>
          </div> -->
        </div>

        <v-divider></v-divider>

        <perfect-scrollbar ref="refChatLogPS" :options="perfectScrollbarOptions" class="ps-chat-log h-full"
          @scroll="chatSCroll">
          <chat-log :key="activeChatRoom.roomKey" :chatroom="activeChatRoom" :isManager="isRoomManager"
            :messages="messages" :user="user" :chat-data="activeChat" :profile-user-avatar="profileUserDataMinimal.avatar"
            @on-delete-message="deleteMessage"></chat-log>
        </perfect-scrollbar>

        <v-form autocomplete="off" @submit.prevent="sendMessage">
          <v-textarea no-resize v-model="chatInputMessage" :placeholder="$t('Kirjoita viestisi')" solo
            class="pb-5 px-5 flex-grow-0 msg-input" height="100" autofocus
            :hint="$t('Voit lähettää viestin Ctrl Enter painikkeilla')" :persistent-hint="false"
            @keydown.enter="onMessageEnter">
            <template #append>
              <!-- <v-btn icon>
                <v-icon>{{ icons.mdiMicrophone }}</v-icon>
              </v-btn> -->
              <twemoji-picker ref="emopicker" :emojiData="emojiDataAll" :emojiGroups="emojiGroups"
                @emojiUnicodeAdded="emojiUnicodeAdded" :pickerWidth="400">
                <template v-slot:twemoji-picker-button>
                  <span id="0" class="emoji-tab active d-none d-lg-flex"><img width="28" class="emoji mr-2"
                      style="cursor: pointer" alt="😀" src="https://twemoji.maxcdn.com/2/72x72/1f600.png" /></span>
                </template>
              </twemoji-picker>

              <v-btn color="primary" elevation="0" type="submit" :icon="$vuetify.breakpoint.xsOnly">
                <template v-if="$vuetify.breakpoint.smAndUp">
                  {{ $t("Lähetä") }}
                </template>
                <template v-else>
                  <v-icon>{{ icons.mdiSendOutline }}</v-icon>
                </template>
              </v-btn>
            </template>
          </v-textarea>
        </v-form>
      </div>

      <!-- Start Conversation -->
      <div v-else class="d-flex align-center justify-center flex-grow-1 flex-column">
        <v-avatar size="109" class="elevation-3 mb-6 bg-card">
          <v-icon size="50" class="rounded-0 text--primary">
            {{ icons.mdiMessageTextOutline }}
          </v-icon>
        </v-avatar>
        <p class="
            mb-0
            px-6
            py-1
            font-weight-medium
            text-lg
            elevation-3
            rounded-xl
            text--primary
            bg-card
          " :class="[{ 'cursor-pointer': $vuetify.breakpoint.smAndDown }]" @click="startConversation">
          {{ $t("Aloita keskustelu") }}
        </p>
      </div>
    </div>
  </v-card>
</template>

<script>
import { useResponsiveLeftSidebar } from "@core/comp-functions/ui";
// eslint-disable-next-line object-curly-newline
import {
  mdiMenu,
  mdiMessageTextOutline,
  mdiPhone,
  mdiVideoOutline,
  mdiMagnify,
  mdiDotsVertical,
  mdiAttachment,
  mdiMicrophone,
  mdiSendOutline,
  mdiInformation,
  mdiAccountOutline
} from "@mdi/js";
import { PerfectScrollbar } from "vue2-perfect-scrollbar";

import {
  ref,
  onUnmounted,
  nextTick,
  computed,
  onMounted,
} from "@vue/composition-api";
import { until, invoke } from "@vueuse/core";
import { getVuetify } from "@core/utils";
import { avatarText } from "@core/utils/filter";
import store from "@/store";
import chatStoreModule from "./chatStoreModule";
import useChat from "./useChat";

// Local Components
import ChatLeftSidebarContent from "./ChatLeftSidebarContent.vue";
import ChatUserProfileSidebarContent from "./ChatUserProfileSidebarContent.vue";
import ChatActiveChatUserProfileSidebarContent from "./ChatActiveChatUserProfileSidebarContent.vue";
import ChatLog from "./ChatLog.vue";
import api from "@/api";
import useAuth from "@/useAuth";
import { TwemojiPicker } from "@kevinfaguiar/vue-twemoji-picker";
import EmojiAllData from "@kevinfaguiar/vue-twemoji-picker/emoji-data/en/emoji-all-groups.json";
import EmojiGroups from "@kevinfaguiar/vue-twemoji-picker/emoji-data/emoji-groups.json";
import router from "@/router";

export default {
  components: {
    PerfectScrollbar,
    ChatLog,
    "twemoji-picker": TwemojiPicker,
    // Local Components
    ChatLeftSidebarContent,
    ChatUserProfileSidebarContent,
    ChatActiveChatUserProfileSidebarContent,
  },
  setup() {
    // const { resolveAvatarBadgeVariant } = useChat();
    const $vuetify = getVuetify();

    const { isLeftSidebarOpen, contentStyles } = useResponsiveLeftSidebar({
      sidebarWidth: 374,
    });

    // * Store module Registration
    // const CHAT_APP_STORE_MODULE_NAME = "app-chat";

    // Register module
    // if (!store.hasModule(CHAT_APP_STORE_MODULE_NAME))
    //   store.registerModule(CHAT_APP_STORE_MODULE_NAME, chatStoreModule);

    // UnRegister on leave
    // onUnmounted(() => {
    //   // if (store.hasModule(CHAT_APP_STORE_MODULE_NAME))
    //   //   store.unregisterModule(CHAT_APP_STORE_MODULE_NAME);
    // });

    // Scroll to Bottom ChatLog
    const refChatLogPS = ref(null);
    const scrollToBottomInChatLog = () => {
      const scrollEl = refChatLogPS.value.$el || refChatLogPS.value;
      scrollEl.scrollTop = scrollEl.scrollHeight;
    };

    // ————————————————————————————————————
    //* ——— User Profile Sidebar
    // ————————————————————————————————————
    // ? Will contain all details of profile user (e.g. settings, about etc.)
    const profileUserData = ref({});

    // ? Will contain id, name and avatar & status
    const profileUserDataMinimal = ref({});

    const isUserProfileSidebarOpen = ref(false);

    // * One time watch for fetching user profile sidebar data
    invoke(async () => {
      await until(isUserProfileSidebarOpen).toBe(true);

      store.dispatch("app-chat/getProfileUser").then((response) => {
        profileUserData.value = response.data;
      });
    });

    // ————————————————————————————————————
    //* ——— Chats & Contacts
    // ————————————————————————————————————
    const chatsContacts = ref([]);
    const contacts = ref([]);

    // ————————————————————————————————————
    //* ——— Single Chat
    // ————————————————————————————————————

    const activeChat = ref({});
    const chatInputMessage = ref("");
    const openChatOfContact = (userId) => {
      // Reset send message input value
      chatInputMessage.value = "";

      store.dispatch("app-chat/getChat", { userId }).then((response) => {
        activeChat.value = response.data;

        // Set unseenMsgs to 0
        const contact = chatsContacts.value.find((c) => c.id === userId);
        if (contact) contact.chat.unseenMsgs = 0;

        // Scroll to bottom
        nextTick(() => {
          scrollToBottomInChatLog();
        });
      });

      // if smAndDown =>  Close Chat & Contacts left sidebar
      if ($vuetify.breakpoint.smAndDown) isLeftSidebarOpen.value = false;
    };

    const sendMessage = () => {
      if (!chatInputMessage.value) return;

      emopicker.value.isPickerOpen = false;

      connection.send(
        JSON.stringify({
          type: "message",
          event: "message",
          message: chatInputMessage.value,
        })
      );

      chatInputMessage.value = "";
      // Scroll to bottom
      nextTick(() => {
        scrollToBottomInChatLog();
      });
    };

    // ————————————————————————————————————
    //* ——— Active Chat
    // ————————————————————————————————————

    const isActiveChatUserProfileSidebarOpen = ref(false);

    // Open Sidebar in smAndDown when "start conversation" is clicked
    const startConversation = () => {
      if ($vuetify.breakpoint.mdAndUp) return;
      isLeftSidebarOpen.value = true;
    };

    // Perfect scrollbar options
    const perfectScrollbarOptions = {
      maxScrollbarLength: 60,
      wheelPropagation: false,
    };

    const emopicker = ref(null);
    const channels = ref([]);
    const managedChannels = ref([]);
    const viewManaged = ref(false);
    const managedRooms = ref([]);
    const isRoomManager = ref(false);

    const selectedChatListItem = ref(null);
    const selectedManagedChannel = ref(null);
    const activeChatRoom = ref(null);
    const searchPerson = ref(false);
    const searchTimeout = ref(null);
    const query = ref("");
    const showSearchBox = ref(false);
    const searchResults = ref([]);

    const messages = ref([]);

    const { user } = useAuth();
    const initialized = ref(false);
    const hasOlder = ref(true);
    const chatIsLoading = ref(false);
    let olderTimeOut = null;
    let shouldCloseConnection = false;
    let connection = null;

    // const privateChannels = computed(() => {
    //   return channels.value.filter(
    //     (c) =>
    //       c.channel_type == 1 &&
    //       !managedChannels.value.find((mc) => mc.id == c.id)
    //   );
    // });

    // const publicChannels = computed(() => {
    //   return channels.value.filter((c) => c.channel_type == 2);
    // });

    const managedPrivateChannels = computed(() => {
      return managedChannels.value.filter((a) => a.channel_type === 1);
    });

    const getManagedChannelRooms = async (channel) => {
      const response = await api.getChannelRooms(channel.id);
      if (response.ok) {
        selectedManagedChannel.value = channel;
        const rooms = response.data;
        rooms.forEach((room) => {
          if (room.chat_channel_type == 2) {
            room.user_name = channel.name;
          }
        });
        managedRooms.value = response.data;
        viewManaged.value = true;
      }
    };

    const viewChannels = () => {
      selectedManagedChannel.value = null;
      viewManaged.value = false;
    };

    const openManagedRoom = (room) => {

      isRoomManager.value = true;
      activeChatRoom.value = {
        roomKey: room.chatroom_key,
        title: selectedManagedChannel.value.name,
        description: selectedManagedChannel.value.description,
        image: room.picture,
        channelId: selectedManagedChannel.value.id,
      };
      selectedChatListItem.value = room.chatroom_key;
      room.unread = false;

      initChatConnection();
    };

    const openChannel = async (channel) => {
      if (channel.channel_type) {
        isRoomManager.value = managedChannels.value.some(
          (a) => a.id === channel.id
        );
      } else {
        isRoomManager.value = false;
      }
      const response = await api.getChannelRoom(channel.id);

      if (response.ok) {
        activeChatRoom.value = {
          roomKey: response.data.chatroom_key,
          title: channel.name,
          description: channel.description,
          image: channel.image,
          channelId: channel.id,
        };
        selectedChatListItem.value = channel.id;
        if (window.innerWidth < 900) {
          chatDrawer = false;
        }
        channel.unread = false;

        initChatConnection();
      }
    };

    const startChatWithUser = async (userId) => {
      const response = await api.getChannelRoomForUser(
        selectedManagedChannel.value.id,
        userId
      );

      if (response.ok) {
        showSearchBox.value = false;
        openManagedRoom(response.data);
        getManagedChannelRooms(selectedManagedChannel.value);
      }
    };

    const chatStarted = async () => {
      if (viewManaged.value) {
        getManagedChannelRooms(selectedManagedChannel.value);
      }
    };

    const initChat = async () => {
      fetchChannels();
      fetchManagedChannels();
    };

    const fetchChannels = async () => {
      const response = await api.getChannels();
      if (response.ok) {
        channels.value = response.data;
      }
    };

    const fetchManagedChannels = async () => {
      const response = await api.getManagedChannels();
      if (response.ok) {
        managedChannels.value = response.data;
      }
    };

    const initChatConnection = async () => {
      if (connection) {
        shouldCloseConnection = true;
        connection.close();
        connection = null;
      }

      chatIsLoading.value = true;
      const response = await api.authenticateChatRoom(
        activeChatRoom.value.roomKey
      );

      const ws = process.env.VUE_APP_WS_BASEURL || "ws://localhost:8000/";
      connection = new WebSocket(
        `${ws}ws/chat/${activeChatRoom.value.roomKey}/?` +
        activeChatRoom.value.roomKey +
        "|" +
        response.data
      );

      connection.onopen = (e) => {
        connection.send(
          JSON.stringify({
            event: "init",
            reverse: true,
          })
        );
      };

      connection.onmessage = onMessage;

      connection.onclose = (e) => {
        if (!shouldCloseConnection) alert("Yhteys katkesi");
      };
    };

    const onMessage = async (e) => {
      const data = JSON.parse(e.data);

      if (data.event == "init_response") {
        // user.value = data.user;
        initialized.value = true;
        messages.value = data.messages;

        nextTick(() => {
          scrollToBottomInChatLog();
        });
        // this.$nextTick(() => {
        //   this.scrollToEnd();

        //   this.$refs.newmessage.focus();
        // });

        let last = data.messages
          .filter((a) => a.user.id != user.value.id)
          .slice(-1)[0];

        if (last) {
          sawMessage(last.id);
        }
      } else if (data.event == "older_response") {
        if (data.messages.length) {
          messages.value = data.messages.concat(messages.value);
        } else {
          hasOlder.value = false;
        }
      } else {
        messages.value.push(data);
        if (messages.value.length == 1) {
          chatStarted();
        }

        nextTick(() => {
          scrollToBottomInChatLog();
        });

        // this.$nextTick(() => {
        //   this.scrollToEnd();
        //   this.$refs.newmessage.focus();
        // });

        if (data.user.id !== user.value.id) {
          sawMessage(data.id);
        }

        // this.$emit("onmessage", data);
      }

      chatIsLoading.value = false;
    };

    const chatSCroll = async () => {
      const scrollEl = refChatLogPS.value.$el || refChatLogPS.value;

      clearTimeout(olderTimeOut);
      if (hasOlder.value && scrollEl.scrollTop < 300) {
        olderTimeOut = setTimeout(() => {
          older();
        }, 300);
      }
    };

    const older = async () => {
      connection.send(
        JSON.stringify({
          event: "older",
          than: messages.value[0].id,
          reverse: true,
        })
      );
    };

    const sawMessage = async (id) => {
      connection.send(
        JSON.stringify({
          type: "message",
          event: "seen",
          message: id,
        })
      );
    };

    const deleteMessage = async (message) => {
      const response = await api.deleteChatMessage(
        activeChatRoom.value.channelId,
        activeChatRoom.value.roomKey,
        message.id
      );
      if (response.ok) {
        var removedMessage = messages.value.find((a) => a.id == message.id);
        if (removedMessage) {
          removedMessage.message = "";
          removedMessage.deleted = true;
        }
      }
    };

    onMounted(() => {
      if (user.value.read_only) {
        router.replace('/home');
      }
      else {
        initChat();
      }
    });

    // const initChat = async () => {
    //   chatIsLoading.value = true;
    //   const response = await api.authenticateChatRoom(props.chatroom.roomKey);

    //   const ws = process.env.WS_BASEURL || "ws://localhost:8000/";
    //   connection = new WebSocket(
    //     `${ws}ws/chat/${props.chatroom.roomKey}/?` +
    //       props.chatroom.roomKey +
    //       "|" +
    //       response.data
    //   );

    //   connection.onopen = (e) => {
    //     connection.send(
    //       JSON.stringify({
    //         event: "init",
    //         reverse: true,
    //       })
    //     );
    //   };

    //   connection.onmessage = onMessage;

    //   connection.onclose = (e) => {
    //     if (!shouldCloseConnection) alert("Yhteys katkesi");
    //   };
    // };

    // onMounted(() => {
    //   initChat();
    // });

    const onMessageEnter = (e) => {
      if (e.ctrlKey) {
        sendMessage();
      }
    };

    const emojiUnicodeAdded = (em) => {
      chatInputMessage.value += em + " ";
    };

    const emojiDataAll = computed(() => {
      return EmojiAllData;
    });

    const emojiGroups = computed(() => {
      return EmojiGroups;
    });

    onUnmounted(() => {
      if (connection) {
        shouldCloseConnection = true;
        connection.close();
      }
    });

    return {
      channels,
      managedChannels,
      getManagedChannelRooms,
      viewChannels,
      viewManaged,
      selectedManagedChannel,
      managedRooms,
      activeChatRoom,
      openChannel,
      openManagedRoom,
      emopicker,
      isRoomManager,
      deleteMessage,
      startChatWithUser,
      managedPrivateChannels,
      chatSCroll,

      messages,
      user,
      initialized,
      hasOlder,
      chatIsLoading,

      onMessageEnter,

      // useChat
      // resolveAvatarBadgeVariant,

      emojiDataAll,
      emojiGroups,
      emojiUnicodeAdded,

      // Layout
      isLeftSidebarOpen,
      contentStyles,

      // Templare ref
      refChatLogPS,

      // User Profile Sidebar
      profileUserDataMinimal,
      profileUserData,
      isUserProfileSidebarOpen,

      // Chat & Contacts
      chatsContacts,
      contacts,

      // Single Chat
      activeChat,
      chatInputMessage,
      openChatOfContact,
      sendMessage,

      // Active Chat
      isActiveChatUserProfileSidebarOpen,

      // startConversation
      startConversation,

      // Perfect Scrollbar
      perfectScrollbarOptions,

      // Filter
      avatarText,

      icons: {
        mdiMenu,
        mdiAccountOutline,
        mdiMessageTextOutline,
        mdiPhone,
        mdiVideoOutline,
        mdiMagnify,
        mdiDotsVertical,
        mdiAttachment,
        mdiMicrophone,
        mdiSendOutline,
        mdiInformation,
      },
    };
  },
};
</script>

<style lang="scss">
@import "~@core/preset/preset/apps/chat.scss";
</style>
